import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Paper,
  Autocomplete,
} from "@mui/material";
import axios from "../utils/axiosInstance";
import {
  STAGES,
  capitalizeFirstLetter,
  formatCurrency,
} from "../utils/columnConfig";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

const STAGE_CONFIG = {
  [STAGES.CONFIRMED]: {
    label: "10 - Confirmed",
    sourceStage: "09",
    amountField: "confirmed_amount",
    amountLabel: "Confirmed Amount",
    amountHelper: "Enter the confirmed amount for this returned claim",
  },
  [STAGES.INVOICED]: {
    label: "11 - Invoiced",
    sourceStage: "10",
    amountField: "invoice_amount",
    amountLabel: "Invoice Amount",
    amountHelper: "Enter the invoice amount for this confirmed claim",
    requiresInvoiceNumber: true,
  },
  [STAGES.PAID]: {
    label: "12 - Paid",
    sourceStage: "11",
    amountField: "paid_amount",
    amountLabel: "Paid Amount",
    amountHelper: "Enter the amount paid for this claim",
  },
};

const AddEntryModal = ({ open, onClose, onSuccess, manufacturer }) => {
  const [stage, setStage] = useState(STAGES.CONFIRMED);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [amount, setAmount] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [previousEntries, setPreviousEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const stageConfig = STAGE_CONFIG[stage];

  // Fetch source entries when modal opens or stage changes
  useEffect(() => {
    if (open) {
      fetchPreviousEntries();
    }
  }, [open, stage, manufacturer]);

  const fetchPreviousEntries = async () => {
    setLoading(true);
    try {
      const sourceStage = stageConfig.sourceStage;
      const params = {
        filter_stage: sourceStage,
        columns:
          "id,filename,file_id,job_id,payer,num_claims,dollar_amount_at_risk,manufacturer,confirmed_amount,invoice_amount",
      };

      // Only add manufacturer filter if not ciita (ciita sees all manufacturers)
      if (manufacturer !== "ciita") {
        params.manufacturer = manufacturer;
      }

      const response = await axios.get(`${API_URL}/reports/detail_list`, {
        params,
        withCredentials: true,
      });
      setPreviousEntries(response.data);
    } catch (err) {
      console.error("Error fetching previous entries:", err);
      setError(err.response?.data?.msg || "Failed to load previous entries");
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = (newStage) => {
    setStage(newStage);
    setSelectedEntry(null);
    setAmount("");
    setInvoiceNumber("");
    setError(null);
  };

  const handleEntrySelect = (entry) => {
    setSelectedEntry(entry);
    setError(null);
  };

  const validateForm = () => {
    if (!selectedEntry) {
      setError("Please select an entry to copy from");
      return false;
    }

    if (!amount || isNaN(amount) || parseFloat(amount) < 0) {
      setError(`Please enter a valid ${stageConfig.amountLabel.toLowerCase()}`);
      return false;
    }

    if (stageConfig.requiresInvoiceNumber && !invoiceNumber.trim()) {
      setError("Please enter an invoice number");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setError(null);

    try {
      const payload = {
        stage,
        source_entry_id: selectedEntry.id,
        [stageConfig.amountField]: parseFloat(amount),
      };

      // Ciita users specify target manufacturer
      if (manufacturer === "ciita" && selectedEntry.manufacturer) {
        payload.manufacturer = selectedEntry.manufacturer;
      }

      // Add invoice number for stage 11
      if (stageConfig.requiresInvoiceNumber) {
        payload.invoice_number = invoiceNumber.trim();
      }

      await axios.post(`${API_URL}/reports/add_entry`, payload, {
        withCredentials: true,
      });

      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Error adding entry:", err);
      setError(err.response?.data?.msg || "Failed to add entry");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setStage(STAGES.CONFIRMED);
    setSelectedEntry(null);
    setAmount("");
    setInvoiceNumber("");
    setError(null);
  };

  const handleClose = () => {
    if (!submitting) {
      resetForm();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Ledger Entry</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}>
          {/* Target Manufacturer Display (Ciita only) */}
          {manufacturer === "ciita" && selectedEntry && (
            <TextField
              fullWidth
              label="Target Ledger (Manufacturer)"
              value={capitalizeFirstLetter(selectedEntry.manufacturer || "N/A")}
              InputProps={{ readOnly: true }}
              helperText={`New entry will be written to the ${capitalizeFirstLetter(
                selectedEntry.manufacturer || "N/A"
              )} ledger.`}
            />
          )}

          {/* Stage Selection */}
          <FormControl fullWidth required>
            <InputLabel>Stage</InputLabel>
            <Select
              value={stage}
              onChange={(e) => handleStageChange(e.target.value)}
              label="Stage"
            >
              {Object.entries(STAGE_CONFIG).map(([key, config]) => (
                <MenuItem key={key} value={key}>
                  {config.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            Select a Stage {stageConfig.sourceStage} entry to create a Stage{" "}
            {stage} entry from:
          </Typography>

          {/* Filename Search */}
          <Autocomplete
            options={previousEntries}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.filename || ""
            }
            value={selectedEntry}
            onChange={(e, value) => handleEntrySelect(value)}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                required
                label="Search by Filename"
                placeholder="Start typing to search..."
                helperText="Type to search and select an entry"
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.filename}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.payer} • {option.num_claims} claims •{" "}
                    {formatCurrency(option.dollar_amount)}
                    {option.manufacturer &&
                      ` • Manufacturer: ${capitalizeFirstLetter(
                        option.manufacturer
                      )}`}
                    {option.file_id && ` • File ID: ${option.file_id}`}
                  </Typography>
                </Box>
              </li>
            )}
          />

          {/* File ID Search */}
          <Autocomplete
            options={previousEntries}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.file_id || ""
            }
            value={selectedEntry}
            onChange={(e, value) => handleEntrySelect(value)}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Or Search by File ID"
                helperText="Alternative way to find an entry"
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box sx={{ width: "100%" }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.file_id}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.filename} • {option.payer}
                    {option.manufacturer &&
                      ` • Manufacturer: ${capitalizeFirstLetter(
                        option.manufacturer
                      )}`}
                  </Typography>
                </Box>
              </li>
            )}
          />

          {/* Selected Entry Details */}
          {selectedEntry && (
            <Paper sx={{ p: 2, bgcolor: "grey.50" }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Entry Details (Read-Only):
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1,
                  mt: 1,
                }}
              >
                <Typography variant="body2">
                  <strong>Filename:</strong> {selectedEntry.filename}
                </Typography>
                <Typography variant="body2">
                  <strong>File ID:</strong> {selectedEntry.file_id || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Job ID:</strong> {selectedEntry.job_id || "-"}
                </Typography>
                <Typography variant="body2">
                  <strong>Payer:</strong> {selectedEntry.payer}
                </Typography>
                <Typography variant="body2">
                  <strong>Claims:</strong> {selectedEntry.num_claims}
                </Typography>
                <Typography variant="body2">
                  <strong>Identified Risk Amount:</strong>{" "}
                  {formatCurrency(selectedEntry.dollar_amount_at_risk)}
                </Typography>
                {(manufacturer === "ciita" || selectedEntry.manufacturer) && (
                  <Typography variant="body2">
                    <strong>Manufacturer:</strong>{" "}
                    {capitalizeFirstLetter(
                      selectedEntry.manufacturer || manufacturer || "-"
                    )}
                  </Typography>
                )}
                {selectedEntry.confirmed_amount && (
                  <Typography variant="body2">
                    <strong>Confirmed Amount:</strong>{" "}
                    {formatCurrency(selectedEntry.confirmed_amount)}
                  </Typography>
                )}
                {selectedEntry.invoice_amount && (
                  <Typography variant="body2">
                    <strong>Invoice Amount:</strong>{" "}
                    {formatCurrency(selectedEntry.invoice_amount)}
                  </Typography>
                )}
              </Box>
            </Paper>
          )}

          {/* Amount Field */}
          <TextField
            required
            fullWidth
            label={stageConfig.amountLabel}
            type="number"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError(null);
            }}
            inputProps={{ min: 0, step: 0.01 }}
            helperText={stageConfig.amountHelper}
            disabled={!selectedEntry}
          />

          {/* Invoice Number Field (Stage 11 only) */}
          {stageConfig.requiresInvoiceNumber && (
            <TextField
              required
              fullWidth
              label="Invoice Number"
              type="text"
              value={invoiceNumber}
              onChange={(e) => {
                setInvoiceNumber(e.target.value);
                setError(null);
              }}
              helperText="Enter the invoice number for this claim"
              disabled={!selectedEntry}
            />
          )}

          {/* Error Message */}
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || !selectedEntry}
        >
          {submitting ? "Adding..." : "Add Entry"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEntryModal;
